import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import "./style.css";
import useCamp from "../../hooks/useCamp";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ViewTypeSwitcher from "../../../components/ViewTypeSwitcher";
import Button from "../../../components/Button";
import TroopItemsDialog from "../../../components/TroopItemsDialog";
import {
  PlayerTroopType,
  TroopItemsDialogStateType,
} from "../../../types/troop";
import { ItemBonusStatEnum, PlayerItemType } from "../../../types/items";
import TroopBox from "../../../components/Boxes/TroopBox";
import ThousandsSeparator from "../../../utils/thousands-separator";
import { CustomIcon } from "../../../components/Icons";
import { roundFloat } from "../../../utils/round-float";
import {
  getStatLine,
  originalBarracksTotal,
  StatModifiers,
} from "../../../utils/statModifiers";
import ModifiedStat from "../../../components/ModifiedStat";
import { Link } from "react-router-dom";
import Dialog from "../../../components/Dialog";
import { Assets } from "../../../constants/assets";
import { MoveTroopType } from "../../types/enum";
import Network, { BattleURL } from "../../../utils/Network";
import { CampInfo } from "../../types";
import { useBarracksUIContext } from "../../../pages/Barracks/BarracksContext";
import CampIndicator from "./camp-indicator";
import { isMobile } from "react-device-detect";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
interface ConfirmDialogType {
  open: boolean;
  submit?: () => void;
  id?: number;
  title: string;
  msg: string;
}
const Camp = (props: any) => {
  const { t } = useAppTranslation(["barracks", "common"]);
  const { fetchTroops, playerTroops, isLoading, isMoveLoading, moveTroop } =
    useCamp();
  const [barrackInfo, setBarracksInfo] = useState<any>();
  const barracksContext = useBarracksUIContext();
  const { campInfo, setCampInfo } = useMemo(() => {
    return {
      campInfo: barracksContext?.campInfo!,
      setCampInfo: barracksContext?.setCampInfo!,
    };
  }, [barracksContext]);
  const axios = new Network();
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogType>({
    open: false,
    title: "",
    msg: "",
  });
  const [viewType, setViewType] = useState(
    +localStorage.getItem("card-sort")! || 1
  );
  const [troopItemsDialog, setTroopItemsDialog] =
    useState<TroopItemsDialogStateType>({
      open: false,
      troop: undefined,
    });

  const handleGetCampInfo = async () => {
    const res = await axios.CallApi<CampInfo>({
      customHost: BattleURL,
      url: "/api/battle/arena/camp-info",
      method: "GET",
    });
    if (res.ok) {
      setCampInfo(res.data);
    }
  };

  const handleGetBarracks = async () => {
    const response = await axios.CallApi({
      url: "/building/info/6",
      method: "get",
    });
    if (response.ok) {
      setBarracksInfo(response?.data);
    }
  };
  useEffect(() => {
    fetchTroops();
    handleGetBarracks();
    handleGetCampInfo();
  }, []);

  function getDetailedStat(
    items: PlayerItemType[],
    value,
    bonusStat: ItemBonusStatEnum,
    statModifiers?: StatModifiers
  ) {
    // While an event modifier runs, the troop field is the original and the stat line holds the value in use.
    const originalTotal = originalBarracksTotal(
      items,
      statModifiers,
      value,
      bonusStat,
      barrackInfo?.bonusStats
    );
    const statLine = getStatLine(statModifiers, bonusStat);
    if (statLine) value = statLine.value;
    const bonusValue = !barrackInfo?.bonusStats
      ? 0
      : (value * barrackInfo?.bonusStats) / 100;
    let itemValue = 0;
    const relatedItem = items?.filter(
      (item) => item.item.bonusStat === bonusStat
    );
    if (relatedItem && relatedItem?.length > 0) {
      for (let key in relatedItem) {
        itemValue += relatedItem[key].bonusValue;
      }
    }
    return {
      value: ThousandsSeparator(value),
      bonusValue: bonusValue,
      itemValue: itemValue,
      total: ThousandsSeparator(value + bonusValue + itemValue),
      originalTotal:
        originalTotal === undefined
          ? undefined
          : ThousandsSeparator(originalTotal),
    };
  }

  function handleOpenItemDialog(troop: PlayerTroopType) {
    setTroopItemsDialog({
      open: true,
      troop,
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-100 min-vh-100 w-100  d-flex flex-column "
    >
      {!isMobile ? (
        <div className="d-flex w-100 flex-row align-items-center justify-content-center flex-wrap-reverse justify-content-md-between">
          <div />
          <CampIndicator value={campInfo?.troopsValue} />
          <ViewTypeSwitcher viewType={viewType} setViewType={setViewType} />
        </div>
      ) : (
        <div>
          <ViewTypeSwitcher viewType={viewType} setViewType={setViewType} />
          <CampIndicator value={campInfo?.troopsValue} />
        </div>
      )}

      <div className="h-100  mt-5  ">
        {!isLoading && playerTroops.length === 0 && (
          <div className=" mt-5 d-flex flex-column align-items-center justify-content-center gap-2">
            <div className="text-center text-white">
              {t("barracks:camp.empty.noTroops")}
            </div>
            <Link to="/barracks">
              <Button variant="wide" className="">
                {t("barracks:camp.empty.moveFromBarracks")}
              </Button>
            </Link>
          </div>
        )}
        <div className="row ">
          {!isLoading &&
            playerTroops?.map((troop: PlayerTroopType) => {
              const damageStats = getDetailedStat(
                troop.items!,
                troop.damage,
                ItemBonusStatEnum.Damage,
                troop.statModifiers
              );
              const armorStats = getDetailedStat(
                troop.items!,
                troop.armor,
                ItemBonusStatEnum.Armor,
                troop.statModifiers
              );
              const magicStats = getDetailedStat(
                troop.items!,
                troop.magicResistance,
                ItemBonusStatEnum.MagicResistance,
                troop.statModifiers
              );
              const rangeStats = getDetailedStat(
                troop.items!,
                troop.range,
                ItemBonusStatEnum.Range
              );
              const healthStats = getDetailedStat(
                troop.items!,
                troop.health,
                ItemBonusStatEnum.Health
              );
              let discountPercentage: boolean | string | number = false;
              if (
                troop.clegUpgradeCost &&
                troop.clegLastUpgradeCost &&
                troop.clegUpgradeCost !== troop.clegLastUpgradeCost
              ) {
                discountPercentage = roundFloat(
                  100 -
                    (troop.clegLastUpgradeCost * 100) / troop.clegUpgradeCost,
                  0,
                  2
                );
              }

              return (
                <div className="col-sm-6 col-12 col-lg-4 col-xl-3">
                  <TroopBox
                    key={troop?.id}
                    {...troop}
                    type={viewType}
                    // leverage={troop.troopId===7?1.5:undefined}
                    refresh={() => fetchTroops()}
                    troop={troop?.troop}
                    bonus={barrackInfo?.bonusStats}
                    damage={
                      <div>
                        <ModifiedStat
                          value={damageStats.total}
                          original={damageStats.originalTotal}
                        />
                        <OverlayTrigger
                          trigger={"click"}
                          rootClose
                          overlay={
                            <Tooltip className="fs-tiny">
                              <b>{damageStats.value}</b>{" "}
                              {t("barracks:stats.damage")}
                              {+damageStats.bonusValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +
                                    {ThousandsSeparator(damageStats.bonusValue)}
                                  </b>{" "}
                                  {t("barracks:stats.bonus")}
                                </>
                              )}
                              {+damageStats.itemValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(damageStats.itemValue)}
                                  </b>{" "}
                                  {t("barracks:stats.itemDamage")}
                                </>
                              )}
                            </Tooltip>
                          }
                        >
                          <span>
                            <CustomIcon
                              style={{ width: 17, height: 17, marginBottom: 1 }}
                              className="pointer ms-1 "
                              icon="info"
                            />
                          </span>
                        </OverlayTrigger>
                      </div>
                    }
                    healthBonus={healthStats.itemValue}
                    armor={
                      <>
                        <ModifiedStat
                          value={armorStats.total}
                          original={armorStats.originalTotal}
                        />
                        <OverlayTrigger
                          trigger={"click"}
                          rootClose
                          overlay={
                            <Tooltip className="fs-tiny">
                              <b>{armorStats.value}</b>{" "}
                              {t("barracks:stats.armor")}
                              {+armorStats.bonusValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(armorStats.bonusValue)}
                                  </b>{" "}
                                  {t("barracks:stats.bonus")}
                                </>
                              )}
                              {+armorStats.itemValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(armorStats.itemValue)}
                                  </b>{" "}
                                  {t("barracks:stats.itemArmor")}
                                </>
                              )}
                            </Tooltip>
                          }
                        >
                          <span>
                            <CustomIcon
                              style={{ width: 17, height: 17, marginBottom: 1 }}
                              className="pointer ms-1 "
                              icon="info"
                            />
                          </span>
                        </OverlayTrigger>
                      </>
                    }
                    magicResistance={
                      <>
                        <ModifiedStat
                          value={magicStats.total}
                          original={magicStats.originalTotal}
                        />
                        <OverlayTrigger
                          trigger={"click"}
                          rootClose
                          overlay={
                            <Tooltip className="fs-tiny">
                              <b>{magicStats.value}</b>{" "}
                              {t("barracks:stats.magicResistance")}
                              {+magicStats.bonusValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(magicStats.bonusValue)}
                                  </b>{" "}
                                  {t("barracks:stats.bonus")}
                                </>
                              )}
                              {+magicStats.itemValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(magicStats.itemValue)}
                                  </b>{" "}
                                  {t("barracks:stats.itemMagicResistance")}
                                </>
                              )}
                            </Tooltip>
                          }
                        >
                          <span>
                            <CustomIcon
                              style={{ width: 17, height: 17, marginBottom: 1 }}
                              className="pointer ms-1 "
                              icon="info"
                            />
                          </span>
                        </OverlayTrigger>
                      </>
                    }
                    range={
                      <>
                        {troop.range}
                        {rangeStats.itemValue > 0 && (
                          <span className="text-info">
                            +{rangeStats.itemValue}
                          </span>
                        )}
                      </>
                    }
                    abilities={troop?.abilities && JSON.parse(troop?.abilities)}
                    action={
                      <>
                        {viewType === 2 && (
                          <div className="dark-yellow text-shadow">
                            {t("barracks:labels.xp")} {troop.xp}/{troop.maxXp}
                          </div>
                        )}

                        <>
                          <OverlayTrigger
                            rootClose
                            trigger="click"
                            placement="top"
                            overlay={
                              <div className="overlay-actions text-white">
                                <motion.div
                                  initial={{
                                    y: 100,
                                    opacity: 0,
                                  }}
                                  animate={{ y: 0, opacity: 1 }}
                                  onClick={() => handleOpenItemDialog(troop)}
                                  transition={{ delay: 0 }}
                                  className="d-flex bt-1  px-2 py-1 justify-content-start pointer align-items-center"
                                >
                                  <img
                                    width={25}
                                    className="me-3"
                                    src="/assets/images/icons/items.png"
                                  />{" "}
                                  {t("barracks:labels.items")}
                                </motion.div>
                                <motion.div
                                  initial={{
                                    y: 100,
                                    opacity: 0,
                                  }}
                                  animate={{ y: 0, opacity: 1 }}
                                  onClick={() =>
                                    setConfirmDialog({
                                      open: true,
                                      submit: () =>
                                        moveTroop(
                                          troop.id,
                                          MoveTroopType.BARRACKS
                                        ).then((res) => {
                                          if (!res.ok) return;
                                          setConfirmDialog((prev) => ({
                                            ...prev,
                                            open: false,
                                          }));
                                          fetchTroops();
                                        }),
                                      msg: t("barracks:messages.moveToBarracksConfirm", {
                                        troop: troop?.troop?.name,
                                      }),
                                      title: t("barracks:labels.areYouSure"),
                                    })
                                  }
                                  transition={{ delay: 0.1 }}
                                  className="d-flex bt-1  px-2 py-1 justify-content-start pointer align-items-center"
                                >
                                  <img
                                    width={20}
                                    className="me-3"
                                    src={Assets.icons.blades}
                                  />{" "}
                                  {t("barracks:camp.actions.moveToBarracks")}
                                </motion.div>
                              </div>
                            }
                          >
                            <div
                              className={viewType === 2 ? "ms-auto" : "mx-auto"}
                            >
                              <Button
                                disabled={troop.state === 4}
                                className="ms-auto p-relative px-0 bg-100"
                                variant="primary"
                              >
                                <img
                                  onClick={(e) =>
                                    troop.state === 4 && e.stopPropagation()
                                  }
                                  src="/assets/images/icons/menu.png"
                                  width={20}
                                />
                                {t("barracks:labels.options")}
                              </Button>
                            </div>
                          </OverlayTrigger>
                        </>
                      </>
                    }
                  />
                </div>
              );
            })}
        </div>
      </div>

      {troopItemsDialog.open && (
        <TroopItemsDialog
          onRefresh={() => {
            fetchTroops();
          }}
          open={troopItemsDialog.open}
          troop={troopItemsDialog.troop}
          onClose={() => {
            setTroopItemsDialog((prev) => ({ ...prev, open: false }));
            fetchTroops();
          }}
        />
      )}

      <Dialog
        loading={Boolean(isMoveLoading)}
        title={confirmDialog?.title}
        closeLabel={t("common:actions.no")}
        submitLabel={t("common:actions.yes")}
        onSubmit={confirmDialog?.submit}
        open={confirmDialog?.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      >
        {confirmDialog?.msg}
      </Dialog>
    </motion.div>
  );
};

export default Camp;
